// WHIT-483 round 2: the rules of the package booking page (P5) and its
// confirmation (P6), kept apart from React so `node --test` can run them.
// The flow is docs/package-tours/api-contract.md operations 7–10, as built in
// taurus-api (§ "As built (WHIT-483 round 1)"). Plain CommonJS.

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const COUNTRY = /^[A-Z]{2}$/;

// Below this age on the day of departure a child travels as an infant.
const INFANT_AGE = 2;

// The travellers the search was for, in the order the tour operator priced
// them: the adults, then each child by age.
const participantTypes = search => [
  ...Array.from({ length: search.adults || 1 }, () => 'ADULT'),
  ...(search.childAges || []).map(age =>
    age < INFANT_AGE ? 'INFANT' : 'CHILD',
  ),
];

const emptyParticipant = type => ({
  type,
  title: '',
  firstName: '',
  lastName: '',
  birthDate: '',
  nationalityCountryCode: 'HU',
});

const emptyForm = search => ({
  booker: {
    firstName: '',
    lastName: '',
    email: '',
    birthDate: '',
    phone: '',
    street: '',
    city: '',
    postCode: '',
    countryCode: 'HU',
    isTravelling: true,
  },
  participants: participantTypes(search).map(emptyParticipant),
  note: '',
  consents: { terms: false, travelConditions: false, marketing: false },
});

// A real calendar date, in the past (or today).
const isPastDate = (value, today) => {
  if (!ISO_DATE.test(value || '')) return false;
  const date = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return false;
  if (date.toISOString().slice(0, 10) !== value) return false;
  return value <= today;
};

// Whole years between a birth date and a day (both YYYY-MM-DD).
const ageOn = (birthDate, day) => {
  const [by, bm, bd] = birthDate.split('-').map(Number);
  const [y, m, d] = day.split('-').map(Number);
  return y - by - (m < bm || (m === bm && d < bd) ? 1 : 0);
};

// The details step's errors, keyed by the create request's path
// (`booker.email`, `participants[1].birthDate`), so a field error from the
// API lands on the same input. Values are translation keys.
/**
 * @param {any} form
 * @param {{ today?: string, departureDate?: string }} [options]
 * @returns {Record<string, string>}
 */
const validateDetails = (form, { today, departureDate } = {}) => {
  /** @type {Record<string, string>} */
  const errors = {};
  const now = today || new Date().toISOString().slice(0, 10);
  const required = (path, value) => {
    if (!String(value || '').trim()) errors[path] = 'This field is required.';
  };
  const { booker } = form;
  ['firstName', 'lastName', 'phone', 'street', 'city', 'postCode'].forEach(
    field => required(`booker.${field}`, booker[field]),
  );
  if (!EMAIL.test(String(booker.email || '').trim())) {
    errors['booker.email'] = 'Enter a valid email address.';
  }
  if (!isPastDate(booker.birthDate, now)) {
    errors['booker.birthDate'] = 'Enter a valid date of birth.';
  } else if (ageOn(booker.birthDate, now) < 18) {
    errors['booker.birthDate'] = 'The person booking must be at least 18.';
  }
  if (!COUNTRY.test(booker.countryCode || '')) {
    errors['booker.countryCode'] = 'This field is required.';
  }

  form.participants.forEach((participant, i) => {
    const at = `participants[${i}]`;
    required(`${at}.title`, participant.title);
    required(`${at}.firstName`, participant.firstName);
    required(`${at}.lastName`, participant.lastName);
    if (!COUNTRY.test(participant.nationalityCountryCode || '')) {
      errors[`${at}.nationalityCountryCode`] = 'This field is required.';
    }
    if (!isPastDate(participant.birthDate, now)) {
      errors[`${at}.birthDate`] = 'Enter a valid date of birth.';
      return;
    }
    // The tour operator priced the party by age: a traveller of another age
    // would be refused (or repriced) after the customer has done all this.
    const day = departureDate || now;
    const age = ageOn(participant.birthDate, day);
    if (participant.type === 'ADULT' && age < 18) {
      errors[`${at}.birthDate`] =
        'An adult must be 18 or older when travelling.';
    } else if (
      participant.type === 'CHILD' &&
      (age < INFANT_AGE || age >= 18)
    ) {
      errors[`${at}.birthDate`] =
        'A child must be between 2 and 17 when travelling.';
    } else if (participant.type === 'INFANT' && age >= INFANT_AGE) {
      errors[`${at}.birthDate`] = 'An infant must be under 2 when travelling.';
    }
  });

  if (!form.consents.terms) {
    errors['consents.terms'] = 'You must accept the terms to book.';
  }
  if (!form.consents.travelConditions) {
    errors['consents.travelConditions'] =
      'You must accept the travel conditions to book.';
  }
  return errors;
};

const BOOKER_TRAVELLER_FIELDS = ['firstName', 'lastName', 'birthDate'];

// Participant 1 is the booker when they travel: their name and birth date
// carry over, so they are not typed twice. Only empty fields are filled.
const withBookerAsTraveller = form => {
  if (!form.booker.isTravelling || !form.participants.length) return form;
  const [first, ...rest] = form.participants;
  if (first.type !== 'ADULT') return form;
  const filled = { ...first };
  BOOKER_TRAVELLER_FIELDS.forEach(field => {
    if (!filled[field]) filled[field] = form.booker[field];
  });
  return { ...form, participants: [filled, ...rest] };
};

// One change to the booker's details, as the form shows it. Ticking "I am
// travelling too" fills traveller 1 at once; while it stays ticked, the
// booker's name and birth date follow into traveller 1 where it still holds
// what was carried over (or nothing), never over something typed there.
const changeBooker = (form, field, value) => {
  const booker = { ...form.booker, [field]: value };
  const changed = { ...form, booker };
  if (field === 'isTravelling') return withBookerAsTraveller(changed);
  const [first, ...rest] = form.participants;
  if (
    !booker.isTravelling ||
    !first ||
    first.type !== 'ADULT' ||
    !BOOKER_TRAVELLER_FIELDS.includes(field) ||
    (first[field] && first[field] !== form.booker[field])
  ) {
    return changed;
  }
  return { ...changed, participants: [{ ...first, [field]: value }, ...rest] };
};

const trimmed = value => String(value || '').trim();

// The create request (operation 7). `choice` is the calculation step:
// { bookingMode, paymentType, roomOptionId }.
const buildCreateInput = ({
  form,
  verification,
  choice,
  locale,
  acknowledgedPrice,
}) => {
  const { booker } = form;
  const input = {
    attemptId: verification.attemptId,
    offerId: verification.offerId,
    token: verification.token || null,
    selectedRoomOptionId: choice.roomOptionId || null,
    booker: {
      firstName: trimmed(booker.firstName),
      lastName: trimmed(booker.lastName),
      email: trimmed(booker.email),
      birthDate: booker.birthDate,
      phone: trimmed(booker.phone),
      address: {
        street: trimmed(booker.street),
        city: trimmed(booker.city),
        postCode: trimmed(booker.postCode),
        countryCode: booker.countryCode,
      },
      isTravelling: !!booker.isTravelling,
    },
    participants: form.participants.map(participant => ({
      type: participant.type,
      title: participant.title,
      firstName: trimmed(participant.firstName),
      lastName: trimmed(participant.lastName),
      birthDate: participant.birthDate,
      nationalityCountryCode: participant.nationalityCountryCode,
    })),
    bookingMode: choice.bookingMode,
    payment: {
      currency: verification.price?.total?.currency || 'EUR',
      // Only FIX pays at booking; the others are paid in full later.
      type:
        choice.bookingMode === 'FIX' && choice.paymentType === 'INSTALMENTS'
          ? 'INSTALMENTS'
          : 'FULL',
    },
    note: trimmed(form.note) || null,
    consents: {
      terms: !!form.consents.terms,
      travelConditions: !!form.consents.travelConditions,
      marketing: !!form.consents.marketing,
    },
    locale: locale === 'hu' ? 'hu' : 'en',
  };
  if (acknowledgedPrice) {
    input.acknowledgedPrice = {
      amount: acknowledgedPrice.amount,
      currency: acknowledgedPrice.currency,
    };
  }
  return input;
};

// Under one attemptId the request must stay the same: allmylespy refuses any
// other difference than the acknowledged price (contract § "Idempotency"),
// so a changed form needs a fresh attemptId from a new check first.
const samePayload = (sent, next) => {
  if (!sent || !next) return false;
  const strip = ({ acknowledgedPrice, ...rest }) => rest;
  return JSON.stringify(strip(sent)) === JSON.stringify(strip(next));
};

// What to pay now for the calculation step's choice, from the check.
const amountNow = (verification, choice) => {
  const schedule = verification?.paymentSchedule;
  const total = schedule?.total || verification?.price?.total || null;
  if (choice.bookingMode !== 'FIX') return null;
  if (
    choice.paymentType === 'INSTALMENTS' &&
    !schedule?.fullPaymentRequired &&
    schedule?.firstPayment
  ) {
    return schedule.firstPayment;
  }
  return total;
};

// Instalments are offered when the check gave a first payment smaller than
// the total and does not insist on paying in full.
const instalmentsOffered = verification => {
  const schedule = verification?.paymentSchedule;
  return (
    !!schedule?.firstPayment &&
    !!schedule.total &&
    !schedule.fullPaymentRequired &&
    schedule.firstPayment.amount < schedule.total.amount
  );
};

// The modes the customer can choose between, in the order shown.
const MODE_ORDER = ['FIX', 'OPTION', 'REQUEST'];
const bookingModes = verification =>
  MODE_ORDER.filter(mode => (verification?.bookingModes || []).includes(mode));

// A request whose answer never arrived. The booking may exist, so it is read
// back by attemptId — never sent again under a new key. api.tipptour.com is
// behind Cloudflare, which gives up after 100 s with a 524 while taurus-api
// is still waiting for the tour operator (up to 110 s).
const UNKNOWN_HTTP = [502, 503, 504, 520, 522, 524];
const isUnknownOutcome = error => {
  if (!error) return false;
  const status = error.response?.status ?? error.status;
  if (UNKNOWN_HTTP.includes(status)) return true;
  if (error.name === 'AbortError' || error.name === 'TimeoutError') return true;
  // fetch() rejects with a TypeError when the connection drops.
  return error.name === 'TypeError' && !error.response;
};

// How long to wait before reading an unknown outcome again: soon at first,
// then every 15 s. The page keeps reading; it never gives up into a failure.
const POLL_DELAYS_MS = [3000, 5000, 8000, 10000];
const pollDelay = attempt =>
  POLL_DELAYS_MS[Math.min(attempt, POLL_DELAYS_MS.length - 1)] +
  (attempt >= POLL_DELAYS_MS.length ? 5000 : 0);

// Codes after which the offer is gone: back to the hotel page (P4).
const OFFER_GONE = ['OFFER_EXPIRED', 'OFFER_UNAVAILABLE', 'NOT_BOOKABLE'];

// What the page does with operation 7's answer.
//   { next: 'pay' }            FIX draft: take the payment
//   { next: 'confirm' }        OPTION/REQUEST draft: send it straight away
//   { next: 'done' }           the booking already went further (a replay)
//   { next: 'inFlight' }       outcome unknown: read it back by attemptId
//   { next: 'priceChanged', price, previousPrice }
//   { next: 'offerGone', code }
//   { next: 'fix', fieldErrors, code, message }  correct, then a new attempt
//   { next: 'enquiry', code, enquiry }  not bookable online: the office
//                              arranges it (WHIT-495)
//   { next: 'retry', code, enquiry }  nothing was created; try again, same
//                              key (the enquiry, if any, as a way out)
//   { next: 'restart', code }  key expired: ask, then a new attempt
//   { next: 'refused', code, message }
const createOutcome = result => {
  if (!result) return { next: 'inFlight' };
  const { booking, error } = result;
  if (!booking) {
    return {
      next: 'refused',
      code: error?.code || null,
      message: error?.message || null,
    };
  }
  if (booking.status === 'IN_FLIGHT') return { next: 'inFlight' };
  if (booking.status === 'DRAFT') {
    return {
      next: booking.nextAction === 'PAYMENT_REQUIRED' ? 'pay' : 'confirm',
    };
  }
  if (booking.status !== 'FAILED') return { next: 'done' };

  const failure = booking.error || {};
  if (failure.code === 'PRICE_CHANGED') {
    return {
      next: 'priceChanged',
      price: failure.price,
      previousPrice: failure.previousPrice,
    };
  }
  // A failure the customer cannot fix: the office arranges it instead
  // (WHIT-495). One that may pass on a retry is retried first.
  const retryable =
    failure.code === 'PROVIDER_UNAVAILABLE' || failure.retryable;
  // An expired key is never an enquiry: the first booking may exist, so the
  // customer is asked before anything else happens (restart).
  if (
    booking.enquiry &&
    !retryable &&
    failure.code !== 'IDEMPOTENCY_KEY_EXPIRED'
  ) {
    return {
      next: 'enquiry',
      code: failure.code || null,
      enquiry: booking.enquiry,
    };
  }
  if (OFFER_GONE.includes(failure.code)) {
    return { next: 'offerGone', code: failure.code };
  }
  if (failure.code === 'IDEMPOTENCY_KEY_EXPIRED') {
    return { next: 'restart', code: failure.code };
  }
  if (retryable) {
    return {
      next: 'retry',
      code: failure.code,
      enquiry: booking.enquiry || null,
    };
  }
  return {
    next: 'fix',
    code: failure.code || null,
    message: failure.message || null,
    fieldErrors: failure.fieldErrors || [],
  };
};

// The API's field errors, by path. A path the form has no input for (or
// DIFFERENT_HOTEL, which concerns the rooms) is kept under '' so the page
// still shows it.
const fieldErrorsByPath = (fieldErrors, knownPaths) => {
  const out = {};
  (fieldErrors || []).forEach(error => {
    const key = error.path && knownPaths.includes(error.path) ? error.path : '';
    out[key] = [out[key], error.message || error.code]
      .filter(Boolean)
      .join(' ');
  });
  return out;
};

// What operation "create a payment order" answered.
//   { next: 'card', order }          show the card form for order.token
//   { next: 'alreadyPaid', orderId } PAYMENT_TAKEN: confirm/record that order
//   { next: 'pending' }              PAYMENT_PENDING: wait and ask again
//   { next: 'refused', code }
const paymentOrderOutcome = result => {
  const code = result?.error?.code;
  if (code === 'PAYMENT_TAKEN' && result.paymentOrder?.id) {
    return { next: 'alreadyPaid', orderId: result.paymentOrder.id };
  }
  if (code === 'PAYMENT_PENDING' || code === 'PAYMENT_IN_PROGRESS') {
    return { next: 'pending' };
  }
  if (
    result?.status === 200 &&
    result.paymentOrder?.id &&
    (result.paymentOrder.token || result.paymentOrder.client_secret)
  ) {
    return { next: 'card', order: result.paymentOrder, amount: result.amount };
  }
  return { next: 'refused', code: code || null };
};

// Where a booking's answer leaves the page. A draft that is only waiting to
// be sent (OPTION, REQUEST) is 'toConfirm', which the page sends once; a send
// already running is 'confirming' and is never sent again. A FIX draft that
// still needs paying drops its payment order: the order may have been paid
// before a confirm was lost, so it is asked for again, and taurus-api then
// answers PAYMENT_TAKEN for it instead of a new card being taken.
const settledState = booking => {
  if (booking.status !== 'DRAFT') {
    return { phase: 'done', booking, waitingFor: null };
  }
  if (booking.nextAction === 'PAYMENT_REQUIRED') {
    return { phase: 'paying', booking, waitingFor: null, order: null };
  }
  return { phase: 'toConfirm', booking, waitingFor: null };
};

const sendsDraft = phase => phase === 'toConfirm';

// What P6 says, from status × nextAction (contract table). A booking that
// was charged and then FAILED is told its money is coming back.
const bookingView = booking => {
  if (!booking) return 'unknown';
  const { status, nextAction } = booking;
  switch (status) {
    case 'CONFIRMED':
      return nextAction === 'PAYMENT_REQUIRED' ? 'acceptedPay' : 'confirmed';
    case 'OPTION':
      return 'option';
    case 'ON_REQUEST':
      return 'onRequest';
    case 'IN_FLIGHT':
      return 'inFlight';
    case 'DRAFT':
      return nextAction === 'PAYMENT_REQUIRED' ? 'draftPay' : 'draftConfirm';
    case 'DECLINED':
      return 'declined';
    case 'EXPIRED':
      return 'expired';
    case 'FAILED':
      return (booking.payments || []).length ||
        (booking.refundsDue || []).length
        ? 'failedRefund'
        : 'failed';
    default:
      return 'unknown';
  }
};

// Amounts to pay are shown to the cent; whole amounts without decimals.
const formatAmount = (money, locale) => {
  if (!money || typeof money.amount !== 'number' || !money.currency) return '';
  const whole = money.amount % 100 === 0;
  return new Intl.NumberFormat(locale === 'hu' ? 'hu-HU' : 'en-GB', {
    style: 'currency',
    currency: money.currency,
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  }).format(money.amount / 100);
};

// The offer chosen on the hotel page (P4), kept for this tab so P5 can show
// what is being booked after a reload: the check (operation 6) returns the
// price, not the dates, room or board. Storage may be missing or refuse
// (private windows): then P5 runs the hotel page's search again instead.
const OFFER_KEY = offerId => `packageOffer:${offerId}`;
const FORM_KEY = offerId => `packageBookingForm:${offerId}`;
const BOOKING_KEY = offerId => `packageBookingStarted:${offerId}`;

// This tab's sessionStorage, or null where there is none: on the server, or
// in a browser that blocks site data, where reading the property itself
// throws. Every helper below takes null and then keeps nothing.
const sessionStore = () => {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
};

const store = (storage, key, value) => {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // Not kept; the page still works without it.
  }
};

const load = (storage, key) => {
  try {
    const raw = storage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const saveOffer = (storage, offer) => {
  if (offer && offer.offerId) store(storage, OFFER_KEY(offer.offerId), offer);
};

const loadOffer = (storage, offerId) => {
  const offer = offerId ? load(storage, OFFER_KEY(offerId)) : null;
  return offer && offer.offerId === offerId ? offer : null;
};

// The details typed so far, so a reload or a trip back to the hotel page
// does not lose them. The consents are not kept: they are given each time.
const saveForm = (storage, offerId, form) => {
  if (!offerId || !form) return;
  store(storage, FORM_KEY(offerId), {
    ...form,
    consents: { terms: false, travelConditions: false, marketing: false },
  });
};

const loadForm = (storage, offerId, search) => {
  const form = offerId ? load(storage, FORM_KEY(offerId)) : null;
  const expected = participantTypes(search);
  // A form for another party (the search changed) starts again.
  if (
    !form ||
    !form.booker ||
    !Array.isArray(form.participants) ||
    form.participants.map(participant => participant.type).join() !==
      expected.join()
  ) {
    return null;
  }
  return form;
};

// The booking this tab has started for an offer. A reload — after paying, in
// the worst case — must pick that booking up again rather than start a new
// one: taurus-api then answers PAYMENT_TAKEN and the payment already made is
// confirmed instead of being left on an unsent draft.
const saveStarted = (storage, offerId, ids) => {
  if (offerId && ids && ids.bookingId && ids.attemptId) {
    store(storage, BOOKING_KEY(offerId), {
      bookingId: ids.bookingId,
      attemptId: ids.attemptId,
    });
  }
};

const loadStarted = (storage, offerId) => {
  const ids = offerId ? load(storage, BOOKING_KEY(offerId)) : null;
  return ids &&
    typeof ids.bookingId === 'string' &&
    typeof ids.attemptId === 'string'
    ? { bookingId: ids.bookingId, attemptId: ids.attemptId }
    : null;
};

// Once the booking has an outcome, this tab forgets it and the typed
// personal details: they are not kept longer than the booking needs them.
const forgetBooking = (storage, offerId) => {
  try {
    storage.removeItem(BOOKING_KEY(offerId));
    storage.removeItem(FORM_KEY(offerId));
  } catch {
    // Nothing kept, or storage refused: either way nothing to forget.
  }
};

// "2 adults, 1 child" — as translation keys with counts.
const partyCounts = search => {
  const types = participantTypes(search);
  return ['ADULT', 'CHILD', 'INFANT']
    .map(type => ({ type, count: types.filter(t => t === type).length }))
    .filter(entry => entry.count > 0);
};

// WHIT-504: what the trip summary shows for the hotel. Its lines are
// placeholders only while the hotel request runs; once it has ended without
// a hotel, the booking's own name stands in, or nothing.
const summaryHotel = ({ hotel, hotelName, hotelLoading }) => ({
  name: hotel?.name || hotelName || null,
  placeholders: !hotel && !!hotelLoading,
});

// Whether the left column opens with a fieldset, whose legend draws its top
// border half a line lower: the trip summary beside it then moves down to
// match (WHIT-505). The details form opens with the booker's fieldset; the
// calculation step with the Room fieldset when rooms are offered and no
// price-change notice sits above it.
const legendLeads = (step, verification) => {
  if (step === 'details') return true;
  if (step !== 'calculation' || !verification) return false;
  return (
    (verification.alternativeRooms || []).length > 0 &&
    !verification.priceChanged
  );
};

module.exports = {
  INFANT_AGE,
  participantTypes,
  emptyForm,
  validateDetails,
  withBookerAsTraveller,
  changeBooker,
  buildCreateInput,
  samePayload,
  amountNow,
  instalmentsOffered,
  bookingModes,
  isUnknownOutcome,
  pollDelay,
  createOutcome,
  fieldErrorsByPath,
  paymentOrderOutcome,
  bookingView,
  settledState,
  sendsDraft,
  formatAmount,
  sessionStore,
  saveOffer,
  loadOffer,
  saveForm,
  loadForm,
  saveStarted,
  loadStarted,
  forgetBooking,
  partyCounts,
  summaryHotel,
  legendLeads,
};
